import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * `TenancySwitcher` — change organisation without signing out, once, for every application (AG-2415).
 *
 * Read `tenancy.ts` first for what the two answers mean. This file draws them.
 *
 * ── THE HOST SUPPLIES TWO CALLS AND ONE CALLBACK, AND NOTHING ELSE ─────────────────────────
 * `loadTenancies` and `switchTenancy` go to the application's OWN backend (`GET /auth/tenancies`,
 * `POST /auth/switch-tenancy`) through the application's own API client, because each one authenticates differently
 * (a bearer here, an HttpOnly cookie there) and that is the host's business. `onSwitched` receives the application's
 * own answer to the switch and applies it — store the new bundle, re-issue a cookie, reload — because only the host
 * knows where its session lives. ⛔ This component never touches a token.
 *
 * ── WHEN IT ASKS ─────────────────────────────────────────────────────────────────────────
 * The launcher fetches on first OPEN, because its button is always drawn. This control is the opposite: it is drawn
 * only for somebody who holds two or more tenancies, so it must ask before it can decide to exist. It asks ONCE per
 * mount — which is once per page load where the host mounts it in its shell. Given a `cacheKey`, the last answer is
 * kept in `sessionStorage` under that key and drawn at once while the new one is fetched (stale-while-revalidate).
 *
 * ⛔ AG-3108 — THE CACHE IS NEVER THE ANSWER, ONLY THE FIRST FRAME. It used to be: kept for the whole tab and
 * dropped only after this control's own switch. Measured on dev 2026-10-13: a person who signed in again into their
 * OTHER organisation (the callback's chooser) was shown the old one as current for the rest of the tab, and the
 * control never asked. Anything that changes the organisation outside this control — a new sign-in, a switch made
 * in another tab (the hosts keep their token in shared `localStorage`), a second organisation granted since — has
 * the same shape. One request per page load is the price of a label that is true.
 *
 * ── WHAT A SWITCH DOES NOT DO ────────────────────────────────────────────────────────────
 * It changes THIS application's session only. The suite sign-in is untouched by design (AG-2415 acceptance 2), so a
 * sibling application keeps the organisation it was opened in until it is switched there too.
 */
import { useCallback, useEffect, useId, useRef, useState, } from 'react';
import { LIST_FAILED_MESSAGE, SWITCH_FAILED_MESSAGE, currentTenancy, readSwitchOutcome, readTenancies, tenancySwitcherIsWorthShowing, } from './tenancy';
const CACHE_PREFIX = 'vsl-tenancies:';
function readCache(key) {
    if (!key || typeof sessionStorage === 'undefined')
        return null;
    try {
        const raw = sessionStorage.getItem(CACHE_PREFIX + key);
        return raw ? JSON.parse(raw) : null;
    }
    catch {
        return null;
    }
}
function writeCache(key, tenancies) {
    if (!key || typeof sessionStorage === 'undefined')
        return;
    try {
        if (tenancies === null)
            sessionStorage.removeItem(CACHE_PREFIX + key);
        else
            sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify(tenancies));
    }
    catch {
        /* storage refused (private mode, quota): the switcher still works, it just asks again */
    }
}
/**
 * One ask for the list, as the mounted switcher makes it — exported so the rule is testable without a DOM.
 *
 * ⛔ AG-3108: it ALWAYS asks; a cached list is the first frame, never a reason to skip the request. On success the
 * answer replaces the cache. On a failure behind a cached list the cache stands and nothing is said (it was true a
 * page ago); a failure with nothing drawn says so.
 */
export async function askForTenancies(loadTenancies, cacheKey) {
    const shown = readCache(cacheKey) !== null;
    try {
        const answer = await loadTenancies();
        if (answer.status >= 200 && answer.status < 300) {
            const list = readTenancies(answer.body);
            writeCache(cacheKey, list);
            return { list, failed: false };
        }
    }
    catch {
        /* falls through to the same answer as a refusal */
    }
    return { list: null, failed: !shown };
}
export function TenancySwitcher({ loadTenancies, switchTenancy, onSwitched, cacheKey, align = 'right', }) {
    const [tenancies, setTenancies] = useState(() => readCache(cacheKey) ?? []);
    const [open, setOpen] = useState(false);
    const [pendingId, setPendingId] = useState(null);
    const [message, setMessage] = useState(null);
    const rootRef = useRef(null);
    const panelId = useId();
    useStyleSheet();
    const refresh = useCallback(async () => {
        const { list, failed } = await askForTenancies(loadTenancies, cacheKey);
        if (list)
            setTenancies(list);
        else if (failed)
            setMessage(LIST_FAILED_MESSAGE);
    }, [cacheKey, loadTenancies]);
    useEffect(() => {
        // The cached list (if any) is already drawn by `useState`'s initialiser; this always asks (AG-3108).
        void refresh();
    }, [refresh]);
    useEffect(() => {
        if (!open)
            return undefined;
        const onDown = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target))
                setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open]);
    const pick = useCallback(async (id) => {
        const target = tenancies.find((t) => t.applicationTenantId === id);
        if (!target || target.isCurrent || pendingId) {
            setOpen(false);
            return;
        }
        setPendingId(id);
        setMessage(null);
        try {
            const answer = await switchTenancy(id);
            const outcome = readSwitchOutcome(answer.status, answer.body);
            if (outcome.kind === 'switched') {
                writeCache(cacheKey, null);
                setOpen(false);
                await onSwitched(outcome.bundle);
            }
            else if (outcome.kind === 'already') {
                setOpen(false);
                await refresh();
            }
            else {
                setMessage(outcome.message);
                if (outcome.kind === 'refused')
                    await refresh();
            }
        }
        catch {
            setMessage(SWITCH_FAILED_MESSAGE);
        }
        finally {
            setPendingId(null);
        }
    }, [cacheKey, onSwitched, pendingId, refresh, switchTenancy, tenancies]);
    if (!tenancySwitcherIsWorthShowing(tenancies))
        return null;
    const onKeyDown = (e) => {
        if (e.key === 'Escape')
            setOpen(false);
    };
    return (_jsx("div", { ref: rootRef, className: "vst-root", onKeyDown: onKeyDown, style: { position: 'relative', display: 'inline-block' }, children: _jsx(TenancySwitcherBody, { tenancies: tenancies, open: open, pendingId: pendingId, message: message, panelId: panelId, align: align, onToggle: () => setOpen((v) => !v), onPick: (id) => void pick(id) }) }));
}
/** The trigger and the menu, without state — what a test renders. */
export function TenancySwitcherBody({ tenancies, open, pendingId, message, panelId, align = 'right', onToggle, onPick, }) {
    if (!tenancySwitcherIsWorthShowing(tenancies))
        return null;
    const current = currentTenancy(tenancies);
    const label = current ? current.organizationName : 'Choose organization';
    return (_jsxs(_Fragment, { children: [_jsxs("button", { type: "button", className: "vst-trigger", "aria-haspopup": "menu", "aria-expanded": open, "aria-controls": open ? panelId : undefined, "aria-label": `Organization: ${label}. Switch organization`, onClick: onToggle, children: [_jsx("span", { className: "vst-trigger-label", children: label }), _jsx("span", { "aria-hidden": "true", className: "vst-caret", children: "\u25BE" })] }), open ? (_jsxs("div", { id: panelId, role: "menu", "aria-label": "Switch organization", className: "vst-panel", style: align === 'left' ? { left: 0 } : { right: 0 }, children: [_jsx("div", { className: "vst-heading", children: "Switch organization" }), tenancies.map((t) => {
                        const busy = pendingId === t.applicationTenantId;
                        return (_jsxs("button", { type: "button", role: "menuitemradio", "aria-checked": t.isCurrent, "aria-busy": busy || undefined, disabled: Boolean(pendingId), className: "vst-item", onClick: () => onPick?.(t.applicationTenantId), children: [_jsx("span", { className: "vst-check", "aria-hidden": "true", children: t.isCurrent ? '✓' : '' }), _jsxs("span", { className: "vst-item-text", children: [_jsx("span", { className: "vst-name", children: t.organizationName }), t.role ? _jsx("span", { className: "vst-role", children: t.role }) : null] }), busy ? _jsx("span", { className: "vst-busy", children: "Switching\u2026" }) : null] }, t.applicationTenantId));
                    }), message ? _jsx("p", { role: "status", className: "vst-inline-message", children: message }) : null, _jsx("p", { className: "vst-note", children: "Switches this application only. Your other Vaarta applications keep their own." })] })) : message ? _jsx("p", { role: "status", className: "vst-message", children: message }) : null] }));
}
const STYLE_ID = 'vst-styles';
function useStyleSheet() {
    useEffect(() => {
        if (typeof document === 'undefined')
            return;
        if (document.getElementById(STYLE_ID))
            return;
        const el = document.createElement('style');
        el.id = STYLE_ID;
        el.textContent = STYLES;
        document.head.appendChild(el);
    }, []);
}
// The launcher's custom properties, so a host that themes `--vsl-*` themes both controls.
const STYLES = `
.vst-root{
  --vsl-bg:#fff; --vsl-fg:#18181b; --vsl-muted:#6b7280; --vsl-border:#e4e4e7;
  --vsl-hover:#f4f4f5; --vsl-ring:#6366f1; --vsl-shadow:0 12px 32px rgba(15,23,42,.18);
  --vsl-radius:12px;
}
@media (prefers-color-scheme: dark){
  .vst-root{
    --vsl-bg:#18181b; --vsl-fg:#fafafa; --vsl-muted:#a1a1aa; --vsl-border:#3f3f46;
    --vsl-hover:#27272a; --vsl-shadow:0 12px 32px rgba(0,0,0,.55);
  }
}
.vst-trigger{
  display:inline-flex; align-items:center; gap:6px; max-width:220px; height:34px; padding:0 10px;
  border:1px solid var(--vsl-border); border-radius:8px; background:transparent; color:var(--vsl-fg);
  font:inherit; font-size:13px; cursor:pointer;
}
.vst-trigger:hover{ background:var(--vsl-hover); }
.vst-trigger:focus-visible, .vst-item:focus-visible{ outline:2px solid var(--vsl-ring); outline-offset:2px; }
.vst-trigger-label{ overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.vst-caret{ color:var(--vsl-muted); font-size:11px; }
.vst-panel{
  position:absolute; z-index:1000; top:calc(100% + 6px); min-width:240px; max-width:min(320px, calc(100vw - 32px));
  padding:6px; background:var(--vsl-bg); color:var(--vsl-fg); border:1px solid var(--vsl-border);
  border-radius:var(--vsl-radius); box-shadow:var(--vsl-shadow);
}
.vst-heading{ padding:6px 8px; font-size:12px; color:var(--vsl-muted); }
.vst-item{
  display:flex; align-items:center; gap:8px; width:100%; padding:8px; border:0; border-radius:8px;
  background:transparent; color:inherit; font:inherit; font-size:14px; text-align:left; cursor:pointer;
}
.vst-item:hover:not(:disabled){ background:var(--vsl-hover); }
.vst-item:disabled{ cursor:progress; opacity:.7; }
.vst-check{ width:14px; flex:none; }
.vst-item-text{ display:flex; flex-direction:column; min-width:0; flex:1; }
.vst-name{ overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.vst-role{ font-size:12px; color:var(--vsl-muted); }
.vst-busy{ font-size:12px; color:var(--vsl-muted); }
.vst-note{ margin:6px 8px 4px; font-size:12px; color:var(--vsl-muted); }
.vst-inline-message{ margin:6px 8px; font-size:13px; }
.vst-message{ position:absolute; top:calc(100% + 6px); right:0; z-index:1000; margin:0; max-width:280px;
  padding:8px 10px; font-size:12px; background:var(--vsl-bg); color:var(--vsl-fg);
  border:1px solid var(--vsl-border); border-radius:8px; box-shadow:var(--vsl-shadow); }
`;
